import { useEffect, useMemo, useState } from "react";
import { Coins, ChevronDown, ArrowLeftRight } from "lucide-react";
import { api } from "../lib/api";
import { COUNTRY_CURRENCY, CURRENCY_NAMES } from "../lib/countries";

const fmt = (n, cur) => {
  if (n == null || !isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("nl-BE", { style: "currency", currency: cur, maximumFractionDigits: n >= 100 ? 0 : 2 }).format(n);
  } catch {
    return `${n.toFixed(2)} ${cur}`;
  }
};

const CurrencySelect = ({ value, onChange, routeCurrencies, all, testId }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
    className="bg-secondary rounded-xl px-2 py-2 text-sm font-mono font-bold outline-none cursor-pointer max-w-[150px]">
    {routeCurrencies.length > 0 && (
      <optgroup label="Op je route">
        {routeCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
      </optgroup>
    )}
    <optgroup label="Alle valuta">
      {all.filter((c) => !routeCurrencies.includes(c)).map((c) => (
        <option key={c} value={c}>{c}{CURRENCY_NAMES[c] ? ` — ${CURRENCY_NAMES[c]}` : ""}</option>
      ))}
    </optgroup>
  </select>
);

export const CurrencyConverter = ({ countries }) => {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState(null);
  const [amount, setAmount] = useState("100");
  const [from, setFrom] = useState("EUR");
  const [to, setTo] = useState("");

  const routeCurrencies = useMemo(() => {
    const list = [];
    (countries || []).forEach((cc) => {
      const cur = COUNTRY_CURRENCY[cc];
      if (cur && !list.includes(cur)) list.push(cur);
    });
    return list;
  }, [countries]);

  useEffect(() => {
    if (to) return;
    setTo(routeCurrencies.find((c) => c !== "EUR") || (routeCurrencies.length ? "USD" : ""));
  }, [routeCurrencies, to]);

  useEffect(() => {
    if (!open || rates) return;
    api.get("/currency/rates").then((r) => setRates(r.data.rates)).catch(() => {});
  }, [open, rates]);

  const all = useMemo(() => (rates ? Object.keys(rates).sort() : [...new Set(["EUR", "USD", "GBP", "CHF", ...routeCurrencies])]), [rates, routeCurrencies]);
  const toCur = to || "USD";
  const result = rates && rates[from] && rates[toCur]
    ? ((parseFloat(String(amount).replace(",", ".")) || 0) / rates[from]) * rates[toCur]
    : null;

  return (
    <div data-testid="currency-converter-card" className="bg-card rounded-2xl mb-4 overflow-hidden">
      <button data-testid="currency-converter-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 p-4 text-left hover:bg-secondary/50 transition-[background-color] duration-200">
        <Coins className="w-4 h-4 text-amber-500 shrink-0" />
        <span className="font-heading font-bold text-sm flex-1">Valuta omrekenen</span>
        {routeCurrencies.filter((c) => c !== "EUR").length > 0 && (
          <span className="text-xs text-muted-foreground font-mono">{routeCurrencies.filter((c) => c !== "EUR").join(" · ")}</span>
        )}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex items-center gap-2">
            <input data-testid="currency-amount-input" type="text" inputMode="decimal" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-24 bg-secondary rounded-xl px-3 py-2 text-sm font-mono font-bold outline-none focus:ring-2 focus:ring-[#B5838D]" />
            <CurrencySelect testId="currency-from-select" value={from} onChange={setFrom} routeCurrencies={routeCurrencies} all={all} />
            <button data-testid="currency-swap-button" type="button" onClick={() => { setFrom(toCur); setTo(from); }}
              title="Wissel valuta om" className="p-2 rounded-full hover:bg-secondary text-muted-foreground shrink-0 transition-[background-color] duration-200">
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <CurrencySelect testId="currency-to-select" value={toCur} onChange={setTo} routeCurrencies={routeCurrencies} all={all} />
          </div>
          <p data-testid="currency-result" className="font-mono font-extrabold text-lg">
            {rates ? <>{fmt(parseFloat(String(amount).replace(",", ".")) || 0, from)} = <span className="text-primary">{fmt(result, toCur)}</span></> : "Koersen laden..."}
          </p>
          {rates && routeCurrencies.filter((c) => c !== "EUR").length > 0 && (
            <p className="text-[11px] text-muted-foreground font-mono">
              {routeCurrencies.filter((c) => c !== "EUR" && rates[c]).map((c) => `€1 = ${rates[c] >= 100 ? Math.round(rates[c]) : rates[c].toFixed(2)} ${c}`).join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
